import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { en, type Dictionary } from './en';

// English only for now (D-0017). Urdu and Hindi are added as more dictionaries with the
// same keys; the courier's choice is saved on their user (`language`).
const dictionaries: Record<string, Dictionary> = { en };

interface I18n {
  t: Dictionary;
  /** fills {name} placeholders */
  f: (text: string, values: Record<string, string | number>) => string;
  formatTime: (iso: string) => string;
  formatDate: (date: string) => string;
}

const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ language = 'en', children }: { language?: string; children: ReactNode }) {
  const t = dictionaries[language] ?? en;
  useEffect(() => {
    document.documentElement.lang = language in dictionaries ? language : 'en';
    document.documentElement.dir = t.dir;
  }, [language, t.dir]);
  const value: I18n = {
    t,
    f: (text, values) => text.replace(/\{(\w+)\}/g, (_, k: string) => String(values[k] ?? '')),
    formatTime: (iso) => new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai' }).format(new Date(iso)),
    formatDate: (date) => new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Dubai' }).format(new Date(`${date}T12:00:00+04:00`)),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useI18n outside I18nProvider');
  return ctx;
}
